import type { CSSProperties, ReactNode } from 'react'
import { format } from '@/i18n/format'
import { useCopy } from '@/i18n/useCopy'
import { PETAL_TOTAL } from './runner'
import styles from './Welcome.module.css'

/**
 * Progress, as the mark opening.
 *
 * The orb is the interface, so it is also the progress. A bar counts a form and
 * says how much of a chore is left; an orb that fills is the assistant becoming
 * more itself as it learns who it is talking to. One petal per answered
 * question, and nothing arrives for a question that was skipped, which is the
 * gentlest possible way of saying that skipping cost something without saying
 * anything at all.
 *
 * Drawn here rather than added to `BlossomRenderer`, and that boundary is the
 * point. The renderer is the mark in every screen of the app and its whole
 * vocabulary is the arc and the dot; teaching it about onboarding would put a
 * concept from one conversation into the shape the whole product is built
 * from, and every other screen would then be rendering a progress indicator
 * that is permanently at zero. The petals are a ring laid over the mark, they
 * are removed when onboarding ends, and the mark underneath never changes.
 *
 * The geometry follows the mark's own. The Namu arc sweeps 270 degrees and is
 * open at the lower right, so the petals live in the 270 degrees the arc
 * occupies and leave the gap alone. A petal in the gap would close the mark,
 * which is the one thing the brand's shape is not allowed to do.
 */

export interface BloomProps {
  /**
   * Answers given so far, or null once there is nothing left to ask.
   *
   * Null rather than zero, because the two mean opposite things. A ring of
   * closed petals says four questions are coming, and a screen reader finding
   * a progress indicator reading nought of four on an ordinary conversation is
   * being told about a task nobody is doing. The wrapper stays either way, so
   * the orb does not shift when the last petal opens.
   */
  petals: number | null
  /**
   * The orb's size, which has to be the same value the orb itself is given.
   * The ring is positioned against this box, so a box that is not the orb's box
   * draws the petals on a circle the mark is not on. Omitted, both fall back to
   * the same default.
   */
  size?: string
  /** The orb. */
  children: ReactNode
}

/**
 * A single petal, drawn pointing up from the centre of a 100 unit box, its base
 * just outside the arc at radius 33 and its tip at radius 48. Every instance is
 * this path rotated, so all four are identical by construction rather than by
 * four sets of coordinates somebody has to keep in step.
 */
const PETAL = 'M 50 2 C 55.6 7.2 55.6 12 50 16 C 44.4 12 44.4 7.2 50 2 Z'

/**
 * Where the petals sit, in degrees clockwise from the top.
 *
 * The arc runs from three o'clock anticlockwise round to six o'clock, which is
 * +90 to -180 in this frame. Four petals are spread across it at even
 * intervals with a margin at each end, so none of them lands on a terminus of
 * the arc or on the Sahel dot resting at the lower one.
 */
const SPAN_START = 90
const SPAN_DEGREES = 270
const ANGLES = Array.from(
  { length: PETAL_TOTAL },
  (_, index) => SPAN_START - (SPAN_DEGREES * (index + 1)) / (PETAL_TOTAL + 1),
)

export function Bloom({ petals, size, children }: BloomProps) {
  const copy = useCopy()

  return (
    <div
      className={styles.bloom}
      style={size === undefined ? undefined : ({ '--bloom-size': size } as CSSProperties)}
    >
      {children}

      {petals === null ? null : (
        <>
          {/*
            Presentational, like the orb it sits on. The count is announced
            through the progressbar below rather than by describing a drawing,
            and the step actually advancing is announced by Namu saying so out
            loud, which everybody gets rather than only screen-reader users.
          */}
          <svg
            className={styles.ring}
            viewBox="0 0 100 100"
            aria-hidden="true"
            focusable={false}
            data-testid="bloom"
          >
            {ANGLES.map((angle, index) => (
              /*
               * The rotation is an SVG attribute on a wrapper and the bloom is
               * a CSS transform on the shape inside it. They cannot share an
               * element: a CSS `transform` overrides the presentation attribute
               * of the same name outright, so animating the petal in place
               * would throw the rotation away and stack all four at twelve
               * o'clock.
               */
              <g key={angle} transform={`rotate(${angle} 50 50)`}>
                <path
                  className={styles.petal}
                  d={PETAL}
                  /*
                   * Fill against outline, not colour alone and not motion
                   * alone. A petal that has arrived is a solid shape and one
                   * that has not is a hairline, so the count survives a
                   * colour-blind reader, a greyscale screen and reduced motion
                   * stopping the bloom.
                   */
                  data-open={index < petals ? '' : undefined}
                  /* Each petal opens a beat after the one before it, in the
                     order they are answered: language, consent, name, place. */
                  style={{ '--petal-index': index } as CSSProperties}
                />
              </g>
            ))}
          </svg>

          {/*
            Not the bar this design rejects. `role="progressbar"` is a semantic
            and not a picture: it is what lets somebody who cannot see the
            petals ask how far along they are, and unlike a live region it says
            nothing at all until it is asked. The counting is still the orb.
          */}
          <span
            className="visually-hidden"
            role="progressbar"
            aria-label={copy.ui.onboarding.progressLabel}
            aria-valuemin={0}
            aria-valuemax={PETAL_TOTAL}
            aria-valuenow={petals}
            aria-valuetext={format(copy.ui.common.of, { current: petals, total: PETAL_TOTAL })}
          />
        </>
      )}
    </div>
  )
}
